import React, { useEffect, useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * generateInsights — takes all interviews, last two interviews' data and first interview data
 * to produce human-readable progress insights.
 */
export const generateInsights = (interviews, latest, previous, first) => {
  const insights = [];
  if (!latest || !previous) return insights;

  const lm = latest.computedMetrics || {};
  const pm = previous.computedMetrics || {};
  const fm = first?.computedMetrics || {};

  // 1. COMMUNICATION (Filler Words)
  const fillerDelta = pm.fillerWordCount !== undefined && lm.fillerWordCount !== undefined ? pm.fillerWordCount - lm.fillerWordCount : 0;
  if (fillerDelta > 0 && pm.fillerWordCount > 0) {
    const pct = Math.round((fillerDelta / pm.fillerWordCount) * 100);
    insights.push({
      id: 'comm-1',
      icon: 'Mic',
      title: 'Filler Words Reduced',
      message: `Your filler words dropped from ${pm.fillerWordCount} to ${lm.fillerWordCount} — that's a ${pct}% reduction!`,
      value: `−${fillerDelta}`,
      positive: true,
      emoji: '🎉',
      category: 'communication',
    });
  } else if (lm.fillerWordCount <= 2) {
    insights.push({
      id: 'comm-2',
      icon: 'CheckCircle',
      title: 'Clean Speech Mastery',
      message: `Excellent! You used only ${lm.fillerWordCount} filler words. Your communication is exceptionally professional and clear.`,
      value: `${lm.fillerWordCount}`,
      positive: true,
      emoji: '✨',
      category: 'communication',
    });
  } else if (fillerDelta < 0) {
    insights.push({
      id: 'comm-3',
      icon: 'AlertCircle',
      title: 'Speech Clarity Tip',
      message: `Filler words increased to ${lm.fillerWordCount}. Practice slow breathing to reduce the urge to fill silence with "um" or "uh".`,
      value: `+${Math.abs(fillerDelta)}`,
      positive: false,
      emoji: '💬',
      category: 'communication',
    });
  }

  // 2. CONFIDENCE (Talk Ratio & WPM)
  const latRatioPct = Math.round((lm.talkRatio || 0) * 100);
  if (latRatioPct >= 40 && latRatioPct <= 65) {
    insights.push({
      id: 'conf-1',
      icon: 'MessageSquare',
      title: 'Confident Engagement',
      message: `Your talk ratio of ${latRatioPct}% is in the "Goldilocks" zone for interviews — balanced and confident.`,
      value: `${latRatioPct}%`,
      positive: true,
      emoji: '✅',
      category: 'confidence',
    });
  } else if (latRatioPct < 40) {
    insights.push({
      id: 'conf-2',
      icon: 'Volume1',
      title: 'Elaboration Opportunity',
      message: `At ${latRatioPct}% talk ratio, you might be answering too briefly. Try the STAR method to expand your responses.`,
      value: `${latRatioPct}%`,
      positive: false,
      emoji: '🗣️',
      category: 'confidence',
    });
  } else {
    insights.push({
      id: 'conf-3',
      icon: 'Volume2',
      title: 'Speaking Balance',
      message: `You spoke ${latRatioPct}% of the time. While confident, ensure you leave room for the interviewer to probe deeper.`,
      value: `${latRatioPct}%`,
      positive: false,
      emoji: '⚖️',
      category: 'confidence',
    });
  }

  // 3. TIME MANAGEMENT (Duration)
  const currentDur = latest.duration || 0;
  const prevDur = previous.duration || 0;
  const durDiff = Math.abs(currentDur - prevDur);
  if (durDiff <= 5 && currentDur >= 15) {
    insights.push({
      id: 'time-1',
      icon: 'Clock',
      title: 'Pacing Consistency',
      message: `You maintained a consistent session duration of ${currentDur} minutes, showing great control over your answering time.`,
      value: `Stable`,
      positive: true,
      emoji: '⏱️',
      category: 'Time Management',
    });
  } else {
    insights.push({
      id: 'time-2',
      icon: 'Activity',
      title: 'Session Dynamics',
      message: `This ${currentDur}m session was ${currentDur > prevDur ? 'more thorough' : 'more concise'} than last time (${prevDur}m). Adaptability is key!`,
      value: `${currentDur}m`,
      positive: true,
      emoji: '📊',
      category: 'Time Management',
    });
  }

  // 4. SKILL ADDED (Improvement or Top Skill)
  const skillDeltas = [];
  const currScores = latest.scores || [];
  const prevScores = previous.scores || [];
  currScores.forEach(cs => {
    const ps = prevScores.find(p => p.name === cs.name);
    if (ps) skillDeltas.push({ name: cs.name, delta: cs.current - ps.current, current: cs.current });
  });

  const biggestJump = [...skillDeltas].sort((a, b) => b.delta - a.delta)[0];
  if (biggestJump && biggestJump.delta > 5) {
    insights.push({
      id: 'skill-1',
      icon: 'PlusCircle',
      title: 'Skill Mastery Growth',
      message: `Your ${biggestJump.name} score jumped by ${biggestJump.delta} points! This shows focused effort on your technical skills.`,
      value: `+${biggestJump.delta}`,
      positive: true,
      emoji: '🚀',
      category: 'skill added',
    });
  } else {
    const topSkill = [...currScores].sort((a, b) => (b.current || 0) - (a.current || 0))[0];
    if (topSkill) {
      insights.push({
        id: 'skill-2',
        icon: 'Award',
        title: 'Core Strength',
        message: `${topSkill.name} is currently your strongest competency with a score of ${topSkill.current}. Keep it sharp!`,
        value: `${topSkill.current}`,
        positive: true,
        emoji: '🏆',
        category: 'skill added',
      });
    }
  }

  return insights;
};

/* ─── Animated counter hook ─── */
const useAnimatedValue = (target, duration = 800) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const start = 0;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(start + (target - start) * eased));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };

    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration]);

  return value;
};

/* ─── Single Insight Card ─── */
const ProgressInsightCard = ({ insight, index = 0 }) => {
  const animDelay = `${index * 100}ms`;
  const numericVal = parseInt(insight.value.replace(/[^-\d]/g, '')) || 0;
  const displayVal = useAnimatedValue(Math.abs(numericVal));
  const formatCategory = (category = '') =>
    category
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const gradients = {
    positive: 'from-emerald-50 to-emerald-50/30',
    negative: 'from-rose-50 to-rose-50/30',
  };

  const accents = {
    positive: {
      border: 'border-emerald-100',
      badge: 'bg-emerald-100 text-emerald-700',
      icon: 'text-emerald-500',
      value: 'text-emerald-600',
    },
    negative: {
      border: 'border-rose-100',
      badge: 'bg-rose-100 text-rose-700',
      icon: 'text-rose-500',
      value: 'text-rose-600',
    },
  };

  const type = insight.positive ? 'positive' : 'negative';
  const colors = accents[type];

  return (
    <div
      className={`group bg-gradient-to-br ${gradients[type]} rounded-2xl border ${colors.border} p-5 
        hover:scale-[1.02] transition-all duration-500 cursor-default relative overflow-hidden`}
      style={{ animationDelay: animDelay, animation: 'fadeSlideUp 0.6s ease-out both' }}
    >
      {/* Decorative glow */}
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${insight.positive ? 'bg-emerald-200/30' : 'bg-rose-200/30'} blur-2xl group-hover:blur-3xl transition-all`} />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-xl ${insight.positive ? 'bg-emerald-500' : 'bg-rose-500'} bg-opacity-10 flex items-center justify-center`}>
              <Icon name={insight.icon} size={16} className={colors.icon} />
            </div>
            <div>
              <span className="text-[11px] font-medium text-slate-500">{formatCategory(insight.category)}</span>
              <h4 className="text-[13px] font-medium text-slate-900 leading-tight">{insight.title}</h4>
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-lg ${colors.badge} text-[12px] font-semibold tabular-nums`}>
            {insight.value.startsWith('+') || insight.value.startsWith('−')
              ? `${insight.value[0]}${displayVal}`
              : displayVal}
          </div>
        </div>

        {/* Message */}
        <p className="text-[12px] text-slate-600 font-normal leading-relaxed">
          {insight.emoji} {insight.message}
        </p>
      </div>
    </div>
  );
};

/**
 * ProgressInsightsGrid — renders the full set of auto-generated insights
 */
export const ProgressInsightsGrid = ({ insights }) => {
  if (!insights || insights.length === 0) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-500">
          <Icon name="Sparkles" size={16} />
        </div>
        <div>
          <h3 className="text-[13px] font-medium text-slate-900">Progress Insights</h3>
          <p className="text-[10px] text-slate-400 font-medium">What changed since your last interview</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {insights.map((insight, idx) => (
          <ProgressInsightCard key={insight.id} insight={insight} index={idx} />
        ))}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProgressInsightCard;
